import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { useRef, useState, useEffect, useCallback } from 'react';

import { linkClasses } from '@mui/material/Link';
import {
  Box,
  Stack,
  Avatar,
  Rating,
  Button,
  Dialog,
  Tooltip,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { fDate } from 'src/utils/format-time';
import { fShortenNumber } from 'src/utils/format-number';

import { useAppDispatch } from 'src/context/store';
import { getTime, getAgeInterval } from 'src/utils';
import { getOrderFunc } from 'src/context/payment/getOrder';
import { afterOrderFunc } from 'src/context/payment/afterOrder';
import { parent_activityFunc } from 'src/context/activity/parent_activity';
import {
  PLACEHOLDER_ACTIVITY_DATE_ISO,
  PLACEHOLDER_ACTIVITY_START_TIME,
} from 'src/constants/activity';

import { Iconify } from 'src/components/iconify';
import { ActivityDetailsDialog } from 'src/components/activity-details-dialog';

import ActivitySingleMap from './components/activity-single-map';
import CreateMessagePopup from './components/create-message-popup';

// ================================================================
// TYPE DEFINITIONS - Tip Tanımlamaları
// ================================================================

/** Ana bileşen prop tipleri */
type Props = {
  product: any;
  disableActions?: boolean;
  data?: any;
  setReviewTabActive?: (value: boolean) => void | undefined;
  entityType: string;
};

/** Ödeme durumu interface'i */
interface PaymentState {
  orderId: string;
  iyzicoUrl: string;
  status: 'idle' | 'processing' | 'success' | 'failed';
  isLoading: boolean;
}

// ================================================================
// MAIN COMPONENT - Ana Bileşen
// ================================================================

export function ProductDetailsSummary({
  product,
  data,
  setReviewTabActive,
  entityType,
  ...other
}: Props) {
  // ================================================================
  // HOOKS VE STATE YÖNETİMİ
  // ================================================================

  const dispatch = useAppDispatch();

  // Redux'tan kullanıcı bilgilerini al
  const { accessToken } = useSelector((state: any) => state.auth);

  // ================================================================
  // LOCAL STATE - Yerel Durum Yönetimi
  // ================================================================

  // UI durum değişkenleri
  const [openCreateMessagePopup, setOpenCreateMessagePopup] = useState(false);
  const [openAllDates, setOpenAllDates] = useState(false);
  const [selectedDates, setSelectedDates] = useState<any[]>([]);
  const [openLocationsDialog, setOpenLocationsDialog] = useState(false);
  const [openDatesDialog, setOpenDatesDialog] = useState(false);
  const [openTimesDialog, setOpenTimesDialog] = useState(false);
  const [openMapDialog, setOpenMapDialog] = useState(false);

  // Ödeme durumu - basitleştirilmiş
  const [paymentState, setPaymentState] = useState<PaymentState>({
    orderId: '',
    iyzicoUrl: '',
    status: 'idle',
    isLoading: false,
  });

  // ================================================================
  // REDUX STATE SELECTORS - Redux Durum Seçicileri
  // ================================================================

  const { createOrderData } = useSelector((state: any) => state.createOrder);
  const { afterOrderData } = useSelector((state: any) => state.afterOrder);
  const { getOrderData } = useSelector((state: any) => state.getOrder);
  const { parent_createFreeReservationData } = useSelector(
    (state: any) => state.parent_createFreeReservation
  );

  // ================================================================
  // REFS - Referanslar (LocalStorage ile Persistence)
  // ================================================================

  // Ödeme polling için referanslar
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Persistent ref'ler - unmount olsa bile değerleri korunur
  const processedOrderIdRef = useRef(localStorage.getItem('hophop_processedOrderId') || '');
  const toastShownRef = useRef(localStorage.getItem('hophop_toastShown') === 'true');
  const currentOrderIdRef = useRef(localStorage.getItem('hophop_currentOrderId') || '');
  const paymentProcessedRef = useRef(localStorage.getItem('hophop_paymentProcessed') === 'true');

  // Ücretsiz rezervasyon için ref'ler
  const freeReservationProcessedRef = useRef(
    localStorage.getItem('hophop_freeReservationProcessed') === 'true'
  );
  const processedFreeReservationIdRef = useRef(
    localStorage.getItem('hophop_processedFreeReservationId') || ''
  );

  // LocalStorage'ı güncelleyen helper fonksiyonlar
  const setProcessedOrderId = (value: string) => {
    processedOrderIdRef.current = value;
    if (value) localStorage.setItem('hophop_processedOrderId', value);
    else localStorage.removeItem('hophop_processedOrderId');
  };

  const setToastShown = (value: boolean) => {
    toastShownRef.current = value;
    if (value) localStorage.setItem('hophop_toastShown', 'true');
    else localStorage.removeItem('hophop_toastShown');
  };

  const setCurrentOrderId = (value: string) => {
    currentOrderIdRef.current = value;
    if (value) localStorage.setItem('hophop_currentOrderId', value);
    else localStorage.removeItem('hophop_currentOrderId');
  };

  const setPaymentProcessed = (value: boolean) => {
    paymentProcessedRef.current = value;
    if (value) localStorage.setItem('hophop_paymentProcessed', 'true');
    else localStorage.removeItem('hophop_paymentProcessed');
  };

  const setFreeReservationProcessed = (value: boolean) => {
    freeReservationProcessedRef.current = value;
    if (value) localStorage.setItem('hophop_freeReservationProcessed', 'true');
    else localStorage.removeItem('hophop_freeReservationProcessed');
  };

  const setProcessedFreeReservationId = (value: string) => {
    processedFreeReservationIdRef.current = value;
    if (value) localStorage.setItem('hophop_processedFreeReservationId', value);
    else localStorage.removeItem('hophop_processedFreeReservationId');
  };

  // ================================================================
  // PAYMENT POLLING FUNCTIONS - Ödeme Polling Fonksiyonları (DEVRE DIŞI)
  // ================================================================

  /** Kontrollü ödeme durumu polling'i başlat */
  const startPaymentPolling = useCallback(
    (orderId: string) => {
      // Önceki polling'i durdur
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      let pollCount = 0;
      const maxPolls = 60; // Maksimum 2 dakika (60 x 2 saniye)

      intervalRef.current = setInterval(() => {
        pollCount++;

        // Maksimum süre aşıldıysa polling'i durdur
        if (pollCount >= maxPolls || paymentProcessedRef.current) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          return;
        }

        // Sadece aktif bir sipariş varsa polling yap
        if (currentOrderIdRef.current === orderId) {
          dispatch(afterOrderFunc({ accessToken, orderId }));
        }
      }, 2000);
    },
    [accessToken, dispatch]
  );

  /** Ödeme durumu kontrolünü durdur */
  const stopPaymentPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // ================================================================
  // EFFECT HOOKS - Yan Etki Kancaları
  // ================================================================

  /** Sipariş oluşturma yanıtını işle */
  useEffect(() => {
    if (createOrderData?.paymentResult?.paymentPageUrl) {
      const newOrderId = createOrderData.order?._id;

      // Eğer zaten bu sipariş işlemdeyse CREATE_ORDER'ı çalıştırma
      if (currentOrderIdRef.current === newOrderId && paymentProcessedRef.current) {
        return;
      }

      // Yeni sipariş başlarken eski localStorage verilerini temizle
      if (
        newOrderId &&
        (currentOrderIdRef.current !== newOrderId ||
          paymentProcessedRef.current ||
          toastShownRef.current)
      ) {
        setProcessedOrderId('');
        setToastShown(false);
        setPaymentProcessed(false);
      }

      const orderId = newOrderId;

      setPaymentState({
        orderId,
        iyzicoUrl: createOrderData.paymentResult.paymentPageUrl,
        status: 'processing',
        isLoading: false,
      });

      // Sadece yeni sipariş için ref'leri set et (MEVCUT DURUMLARI KORUYARAK)
      setProcessedOrderId('');
      setCurrentOrderId(orderId);
      // toastShownRef ve paymentProcessedRef'i SIFIRLAMA - çünkü loop yaratır!

      // Kontrollü polling başlat
      startPaymentPolling(orderId);
    }
  }, [
    createOrderData?.paymentResult?.paymentPageUrl,
    createOrderData?.order?._id,
    startPaymentPolling,
  ]);

  /** Sipariş sonrası yanıtını işle (polling sonucu) */
  useEffect(() => {
    if (
      afterOrderData?.order?._id &&
      afterOrderData.order._id !== processedOrderIdRef.current &&
      currentOrderIdRef.current &&
      !paymentProcessedRef.current
    ) {
      setProcessedOrderId(afterOrderData.order._id);
      stopPaymentPolling(); // Polling'i durdur
      // paymentProcessedRef.current'ı GET_ORDER'da set edeceğiz

      // Son durum kontrolü yap
      dispatch(getOrderFunc({ accessToken, orderId: currentOrderIdRef.current }));
    }
  }, [afterOrderData?.order?._id, accessToken, dispatch]);

  /** Ödeme sonucunu işle */
  useEffect(() => {
    // Erken return kontrolları
    if (
      !getOrderData?.paymentStatus ||
      !currentOrderIdRef.current ||
      paymentProcessedRef.current ||
      toastShownRef.current
    ) {
      return;
    }

    const paymentStatus = getOrderData.paymentStatus;

    // ÖNCE ref'leri set et ki tekrar çalışmasın
    setPaymentProcessed(true);
    setToastShown(true);

    // Ödeme başarılı
    if (paymentStatus === 'success') {
      setPaymentState((prev) => ({ ...prev, status: 'success', iyzicoUrl: '' }));

      toast.success('Ödeme başarılı. Rezervasyonunuz alınmıştır.', {
        position: 'bottom-right',
      });

      dispatch(parent_activityFunc({ activityId: product._id ?? '' }));

      // Kısa bir gecikme sonrası sayfayı yenile
      setTimeout(() => {
        window.location.reload();
      }, 1500);

      return;
    }

    // Ödeme başarısız
    if (paymentStatus === 'fail') {
      setPaymentState((prev) => ({ ...prev, status: 'failed', iyzicoUrl: '' }));

      toast.error('Ödeme başarısız. Lütfen tekrar deneyin.', {
        position: 'bottom-right',
      });
    }
  }, [getOrderData?.paymentStatus, product._id]);

  /** Ücretsiz rezervasyon işlemini işle */
  useEffect(() => {
    if (parent_createFreeReservationData?._id) {
      const newReservationId = parent_createFreeReservationData._id;

      // Eğer zaten bu rezervasyon işlemdiyse atla
      if (
        processedFreeReservationIdRef.current === newReservationId &&
        freeReservationProcessedRef.current
      ) {
        return;
      }

      // Yeni rezervasyon başlarken eski localStorage verilerini temizle
      if (
        processedFreeReservationIdRef.current !== newReservationId ||
        freeReservationProcessedRef.current
      ) {
        setFreeReservationProcessed(false);
      }

      // Ref'leri set et ki tekrar çalışmasın
      setProcessedFreeReservationId(newReservationId);
      setFreeReservationProcessed(true);

      toast.success('Rezervasyonunuz alınmıştır.', {
        position: 'bottom-right',
      });

      // Loading durumunu kapat
      setPaymentState((prev) => ({ ...prev, isLoading: false }));

      dispatch(parent_activityFunc({ activityId: product._id ?? '' }));
    }
  }, [parent_createFreeReservationData?._id, product._id]);

  /** Bileşen kaldırıldığında temizlik yap */
  useEffect(
    () => () => {
      stopPaymentPolling();
      // Sadece polling temizle, localStorage ref'leri korunur
    },
    [stopPaymentPolling]
  );

  /** Sayfa ilk yüklendiğinde eski localStorage verilerini temizle */
  useEffect(() => {
    const cleanupOldData = () => {
      const currentTime = Date.now();
      const lastCleanup = localStorage.getItem('hophop_lastCleanup');

      // 1 dakika geçmişse localStorage'ı temizle
      if (!lastCleanup || currentTime - parseInt(lastCleanup) > 1 * 60 * 1000) {
        localStorage.removeItem('hophop_processedOrderId');
        localStorage.removeItem('hophop_toastShown');
        localStorage.removeItem('hophop_currentOrderId');
        localStorage.removeItem('hophop_paymentProcessed');
        localStorage.removeItem('hophop_freeReservationProcessed');
        localStorage.removeItem('hophop_processedFreeReservationId');
        localStorage.setItem('hophop_lastCleanup', currentTime.toString());
      }
    };

    cleanupOldData();
  }, []);

  // ================================================================
  // EVENT HANDLERS - Olay İşleyicileri
  // ================================================================

  /** Ana rezervasyon işleyicisi */
  // const handleReservation = useCallback(async () => {
  //   // Kullanıcı girişi kontrolü
  //   if (!accessToken) {
  //     toast.warning('Rezervasyon yapabilmek için giriş yapmanız gerekmektedir.', {
  //       position: 'bottom-right',
  //     });
  //     return;
  //   }

  //   setPaymentState((prev) => ({ ...prev, isLoading: true }));

  //   try {
  //     if (!product?.price) {
  //       // Ücretsiz rezervasyon
  //       dispatch(parent_createFreeReservationFunc({ activityId: product._id, accessToken }));
  //     } else {
  //       // Ücretli rezervasyon
  //       await dispatch(
  //         updateUserPaymentFunc({
  //           accessToken,
  //           city: userData?.contact?.address?.city ?? 'İstanbul',
  //           description: product.title ?? 'Hophop Rezervasyon',
  //           email: authData?.user?.email,
  //           identificationNumber: '11111111111',
  //           ip: '',
  //           name: userData?.personalInformation?.name ?? 'Anonim',
  //           postcode: userData?.contact?.address?.zipcode ?? '34000',
  //           surname: userData?.personalInformation?.surname ?? 'Kullanıcı',
  //         })
  //       );

  //       dispatch(createOrderFunc({ activityId: product._id, accessToken }));
  //     }
  //   } catch (error) {
  //     toast.error('Rezervasyon sırasında bir hata oluştu.');
  //     setPaymentState((prev) => ({ ...prev, isLoading: false }));
  //   }
  // }, [accessToken, product, userData, authData, dispatch]);

  const handleReservation = useCallback(() => {
    // External link varsa linke git
    if (product.externalLink) {
      const url = product.externalLink.includes('http')
        ? product.externalLink
        : `https://${product.externalLink}`;
      window.open(url, '_blank');
      return;
    }

    // External telefon numarası varsa arama yap
    if (product.externalPhoneNumber) {
      window.open(`tel:${product.externalPhoneNumber}`, '_self');
      return;
    }

    // External mail varsa mail gönderme ekranını aç
    if (product.externalMail) {
      window.open(
        `mailto:${product.externalMail}?subject=Rezervasyon%20Talebi&body=Merhaba,%20${product.title}%20için%20rezervasyon%20yapmak%20istiyorum.`,
        '_self'
      );
      return;
    }

    // Eğer hiçbiri yoksa normal rezervasyon işlemi (şimdilik boş)
  }, [product]);

  /** Ödeme diyaloğunu kapat */
  const handleClosePaymentDialog = useCallback(() => {
    stopPaymentPolling();
    setPaymentState((prev) => ({ ...prev, iyzicoUrl: '' }));

    // Dialog kapatıldığında son bir kontrol yap (sadece henüz işlenmemişse)
    if (currentOrderIdRef.current && !paymentProcessedRef.current) {
      dispatch(getOrderFunc({ accessToken, orderId: currentOrderIdRef.current }));
    }

    // Ref'leri temizleme - KALDIRILDI (loop yaratıyordu)

    if (paymentState.status === 'success') {
      window.location.reload();
    }
  }, [paymentState.status, stopPaymentPolling, accessToken, dispatch]);

  // ================================================================
  // RENDER FUNCTIONS - Render Fonksiyonları
  // ================================================================

  /** Paylaşım butonlarını render et */
  const renderShare = () => (
    <Box
      sx={{
        gap: 1.5,
        display: 'flex',
        justifyContent: 'center',
        [`& .${linkClasses.root}`]: {
          gap: 1,
          alignItems: 'center',
          display: 'inline-flex',
          color: 'text.secondary',
          typography: 'subtitle2',
        },
      }}
    >
      {/* Yorum butonu - sadece aktivite tipinde ve giriş yapılmışsa */}
      {accessToken && (
        <Tooltip title="Yorumlar" arrow>
          <Button
            variant="outlined"
            color="primary"
            sx={{ minWidth: 0, width: 56, height: 56, borderRadius: 2 }}
            onClick={() => setReviewTabActive?.(true)}
          >
            <Iconify icon="iconamoon:comment" width={26} />
          </Button>
        </Tooltip>
      )}

      {/* Müşteri hizmetleri butonu - sadece aktivite tipinde ve giriş yapılmışsa */}
      {accessToken && entityType === 'aktivite' && (
        <Tooltip title="Organizatöre Sor" arrow>
          <Button
            variant="outlined"
            color="warning"
            sx={{ minWidth: 0, width: 56, height: 56, borderRadius: 2 }}
            onClick={() => setOpenCreateMessagePopup(true)}
          >
            <Iconify icon="hugeicons:customer-service-01" width={26} />
          </Button>
        </Tooltip>
      )}

      {/* Link paylaşma butonu */}
      <Tooltip title="Paylaş" arrow>
        <Button
          variant="outlined"
          color="primary"
          sx={{ minWidth: 0, width: 56, height: 56, borderRadius: 2 }}
          onClick={() => {
            navigator.clipboard.writeText(`https://hophop.com.tr${window.location.pathname}`);
            toast.success('Link kopyalandı');
          }}
        >
          <Iconify icon="uil:share" width={26} />
        </Button>
      </Tooltip>
    </Box>
  );

  /** Alt açıklama metnini render et */
  const renderSubDescription = () => {
    const description = product?.listing?.description ?? product.additionalDetails;
    const isLongDescription = description && description.length > 200; // Yaklaşık 4 satır için eşik değeri

    const handleScrollToDescription = () => {
      // Description bölümüne scroll yap
      const descriptionElement = document.querySelector('[data-description-section]');
      if (descriptionElement) {
        descriptionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback: sayfanın aşağısına scroll yap
        window.scrollTo({ behavior: 'smooth', top: window.innerHeight * 2 });
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flexWrap: 'wrap' }}>
        <Typography
          variant="h6"
          fontFamily="Rubik"
          fontWeight={400}
          sx={{
            color: 'text.secondary',
            lineHeight: '29px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            flex: 1,
            minWidth: 0,
          }}
        >
          {description}
        </Typography>
        {isLongDescription && (
          <Box
            component="span"
            onClick={handleScrollToDescription}
            sx={{
              color: 'primary.main',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-block',
              whiteSpace: 'nowrap',
              alignSelf: 'flex-end',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Devamı...
          </Box>
        )}
      </Box>
    );
  };

  /** Değerlendirme yıldızlarını render et */
  const renderRating = () => (
    <Box
      sx={{
        display: 'flex',
        typography: 'body2',
        alignItems: 'center',
        justifyContent: 'flex-end',
        color: 'text.disabled',
        width: '35%',
      }}
    >
      <Rating
        size="small"
        value={product?.listing?.rating?.average || 0}
        readOnly
        sx={{ mr: 1, color: 'primary.main' }}
      />
      {`(${fShortenNumber(product?.listing?.rating?.numberOfReviews)} yorum)`}
    </Box>
  );

  /** Organizatör bilgilerini render et */
  const renderAuthor = () => {
    if (!data) return null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '65%' }}>
        <Avatar alt={data?.name} src={data?.imageUrl} sx={{ width: 56, height: 56 }} />
        <Stack direction="column" gap={0.5}>
          <Typography variant="h6" fontFamily="Rubik" fontWeight={500}>
            {data?.name}
          </Typography>
          <Typography
            variant="caption"
            fontFamily="Rubik"
            fontWeight={400}
            sx={{ color: 'primary.main' }}
          >
            Organizatör
          </Typography>
        </Stack>
      </Box>
    );
  };

  /** Aktivite detaylarını render et */
  const renderActivityDetails = () => {
    const hasLocationInfo =
      product?.listing?.format === 'online' ||
      product?.location?.district ||
      product?.location?.city ||
      (product as any)?.hasMultipleLocations;

    const activityDetails = [
      {
        icon: 'f7:person',
        color: '#73B99A',
        label:
          product?.listing?.ageInterval && product?.listing?.ageInterval.length > 0
            ? getAgeInterval(product?.listing?.ageInterval)
            : 'Tüm Yaşlar',
      },
      ...(product?.schedule?.openHour
        ? [
            {
              icon: 'tabler:clock',
              color: 'warning.main',
              label: `${getTime(product?.schedule?.openHour)}${
                product?.schedule?.closeHour ? ` - ${getTime(product?.schedule?.closeHour)}` : ''
              }`,
            },
          ]
        : []),
    ];

    const renderLocationBlock = () => {
      if (!hasLocationInfo) {
        return null;
      }

      if (product?.listing?.format === 'online') {
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <Iconify
              icon="proicons:location"
              width={24}
              sx={{ color: 'error.main', visibility: 'hidden' }}
            />
            <Box
              sx={{
                backgroundColor: 'success.main',
                py: 0.5,
                px: 1.5,
                borderRadius: 1,
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              Online
            </Box>
          </Stack>
        );
      }

      if ((product as any)?.hasMultipleLocations) {
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <Iconify icon="proicons:location" width={24} sx={{ color: 'error.main' }} />
            <Button
              variant="text"
              sx={{ p: 0, minWidth: 'auto' }}
              onClick={() => setOpenLocationsDialog(true)}
            >
              <Typography variant="body1">Konumları Gör</Typography>
            </Button>
          </Stack>
        );
      }

      if (product?.location?.district || product?.location?.city) {
        return (
          <Stack direction="row" alignItems="flex-start" gap={1}>
            <Iconify icon="proicons:location" width={24} sx={{ color: 'error.main' }} />
            <Typography
              variant="body1"
              fontFamily="Rubik"
              fontWeight={400}
              sx={{ color: 'text.secondary' }}
            >
              <Box>
                {(() => {
                  const title = product.location?.address?.title;
                  const district = product.location?.district;
                  const city = product.location?.city;
                  
                  const locationText = district === city
                    ? city
                    : district && city
                      ? `${district}/${city}`
                      : district || city || '';
                  
                  return (
                    <>
                      {title && (
                        <Box component="span" display="block">
                          {title}
                        </Box>
                      )}
                      {locationText && (
                        <Box component="span" display="block">
                          {locationText}
                        </Box>
                      )}
                    </>
                  );
                })()}
                {product?.location?.address?.description && (
                  <Box component="span" display="block">
                    {product?.location?.address?.description}
                  </Box>
                )}
              </Box>
            </Typography>
          </Stack>
        );
      }

      return null;
    };

    const renderScheduleBlock = () => {
      const hasValidSchedule =
        product?.schedule?.startDate &&
        product.schedule.startDate !== PLACEHOLDER_ACTIVITY_DATE_ISO;

      const scheduleDays = product?.schedule?.days ?? [];
      const primaryDay = scheduleDays[0];
      const hasMultipleDays = scheduleDays.length > 1;
      
      // Çoklu aktivite kontrolü - eğer çoklu aktivite varsa butonları göster
      const hasMultipleDates = (product as any)?.hasMultipleDates;
      const hasMultipleTimes = (product as any)?.hasMultipleTimes;

      // Çoklu aktivite varsa direkt butonları göster
      if (hasMultipleDates || hasMultipleTimes || hasMultipleDays) {
        const renderTimeSuffix = () => {
          if (hasMultipleTimes) {
            return (
              <Button
                variant="text"
                sx={{ p: 0, ml: 0.5, minWidth: 'auto' }}
                onClick={() => setOpenTimesDialog(true)}
              >
                <Typography variant="body2">Saatleri Gör</Typography>
              </Button>
            );
          }

          const hasValidStartTime =
            primaryDay?.startTime != null &&
            primaryDay?.startTime !== PLACEHOLDER_ACTIVITY_START_TIME;

          if (!hasValidStartTime) {
            if (primaryDay?.startTime === PLACEHOLDER_ACTIVITY_START_TIME) {
              return ' - Saat Sor';
            }
            return '';
          }

          if (primaryDay?.startTime === 100) {
            return ` - ${getTime(primaryDay.startTime)}`;
          }

          if (hasValidStartTime) {
            return ` - ${getTime(primaryDay.startTime)}`;
          }

          return '';
        };

        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <Iconify icon="solar:calendar-linear" width={24} sx={{ color: 'primary.main' }} />
            {hasMultipleDates ? (
              <Button variant="text" sx={{ p: 0 }} onClick={() => setOpenDatesDialog(true)}>
                <Typography variant="body1">Tarihleri Gör</Typography>
              </Button>
            ) : hasMultipleDays ? (
              <Button
                variant="text"
                sx={{ p: 0 }}
                onClick={() => {
                  setSelectedDates(scheduleDays);
                  setOpenAllDates(true);
                }}
              >
                <Typography variant="body1">Tarihleri Gör</Typography>
              </Button>
            ) : hasValidSchedule ? (
              <Typography
                variant="body1"
                fontFamily="Rubik"
                fontWeight={400}
                sx={{ color: 'text.secondary' }}
              >
                {`${fDate(product?.schedule?.startDate)}`}
                {renderTimeSuffix()}
              </Typography>
            ) : (
              <Typography
                variant="body1"
                fontFamily="Rubik"
                fontWeight={400}
                sx={{ color: 'text.secondary' }}
              >
                Bilgi Al
              </Typography>
            )}
          </Stack>
        );
      }

      // Çoklu aktivite yoksa ve geçerli tarih yoksa "Bilgi Al" göster
      if (!hasValidSchedule) {
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <Iconify icon="solar:calendar-linear" width={24} sx={{ color: 'primary.main' }} />
            <Typography
              variant="body1"
              fontFamily="Rubik"
              fontWeight={400}
              sx={{ color: 'text.secondary' }}
            >
              Bilgi Al
            </Typography>
          </Stack>
        );
      }

      const renderTimeSuffix = () => {
        const hasValidStartTime =
          primaryDay?.startTime != null &&
          primaryDay?.startTime !== PLACEHOLDER_ACTIVITY_START_TIME;

        if ((product as any)?.hasMultipleTimes) {
          return (
            <Button
              variant="text"
              sx={{ p: 0, ml: 0.5, minWidth: 'auto' }}
              onClick={() => setOpenTimesDialog(true)}
            >
              <Typography variant="body2">Saatleri Gör</Typography>
            </Button>
          );
        }

        if (!hasValidStartTime) {
          if (primaryDay?.startTime === PLACEHOLDER_ACTIVITY_START_TIME) {
            return ' - Saat Sor';
          }
          return '';
        }

        if (primaryDay?.startTime === 100) {
          return ` - ${getTime(primaryDay.startTime)}`;
        }

        if (hasValidStartTime) {
          return ` - ${getTime(primaryDay.startTime)}`;
        }

        return '';
      };

      return (
        <Stack direction="row" alignItems="center" gap={1}>
          <Iconify icon="solar:calendar-linear" width={24} sx={{ color: 'primary.main' }} />
          {(product as any)?.hasMultipleDates ? (
            <Button variant="text" sx={{ p: 0 }} onClick={() => setOpenDatesDialog(true)}>
              <Typography variant="body1">Tarihleri Gör</Typography>
            </Button>
          ) : hasMultipleDays ? (
            <Button
              variant="text"
              sx={{ p: 0 }}
              onClick={() => {
                setSelectedDates(scheduleDays);
                setOpenAllDates(true);
              }}
            >
              <Typography variant="body1">Tarihleri Gör</Typography>
            </Button>
          ) : (
            <Typography
              variant="body1"
              fontFamily="Rubik"
              fontWeight={400}
              sx={{ color: 'text.secondary' }}
            >
              {`${fDate(product?.schedule?.startDate)}`}
              {renderTimeSuffix()}
            </Typography>
          )}
        </Stack>
      );
    };

    return (
      <Stack direction="column" gap={1}>
        {renderScheduleBlock()}
        {renderLocationBlock()}
        {activityDetails.map((item, index) => (
          <Stack key={item.icon + index} direction="row" alignItems="flex-start" gap={1}>
            <Iconify icon={item.icon} width={24} sx={{ color: item.color }} />
            <Typography
              variant="body1"
              fontFamily="Rubik"
              fontWeight={400}
              sx={{ color: 'text.secondary' }}
            >
              {item.label}
            </Typography>
          </Stack>
        ))}

        {/* Haritada göster butonu - sadece online değilse */}
        {product?.listing?.format !== 'online' && (
          <Stack
            direction="row"
            alignItems="flex-start"
            gap={1}
            sx={{ cursor: 'pointer' }}
            onClick={() => setOpenMapDialog(true)}
          >
            <Iconify icon="tabler:map-pin" width={24} sx={{ visibility: 'hidden' }} />
            <Box
              sx={{
                backgroundColor: '#C9E3C8',
                py: 0.5,
                px: 1.5,
                borderRadius: 1,
                fontWeight: 'bold',
                color: '#07406C',
                width: '150px',
                textAlign: 'center',
              }}
            >
              Haritada Gör
            </Box>
          </Stack>
        )}
      </Stack>
    );
  };

  /** Rezervasyon butonunu render et */
  const renderReservationButton = () => {
    // Aktivite tipindeyse
    if (
      entityType === 'blog'
        ? product?.externalLink || product?.externalPhoneNumber || product?.externalMail
        : true
    ) {
      return (
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ width: '100%', mt: 'auto' }}
          onClick={handleReservation}
          // disabled={isLoading || !!product?.reservation}
        >
          {/* {isLoading ? (
            <CircularProgress size={24} />
          ) : ( */}
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h5" fontFamily="Rubik" fontWeight={800}>
              {product?.fee === 'free'
                ? 'Ücretsiz'
                : product?.price
                  ? `${product?.price} TL`
                  : entityType === 'blog'
                    ? 'Bilgi Al'
                    : 'Fiyat Al  '}
            </Typography>

            <Typography variant="h6" fontFamily="Rubik" fontWeight={500}>
              - Rezervasyon Yap
            </Typography>
          </Stack>
          {/* )} */}
        </Button>
      );
    }

    return null;
  };

  // ================================================================
  // MAIN RENDER - Ana Render
  // ================================================================

  return (
    <Stack spacing={3} {...other}>
      {/* Ana içerik alanı */}
      <Stack spacing={2} alignItems="flex-start" sx={{ aspectRatio: 1 }}>
        {/* Başlık ve paylaşım butonları */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Box>
            <Typography variant="h5" fontFamily="Rubik" fontWeight={500}>
              {product?.title ? product?.title : product?.listing?.title}
            </Typography>
            <Typography
              variant="caption"
              fontFamily="Rubik"
              fontWeight={400}
              sx={{ color: 'text.secondary' }}
            >
              {/* {capitalize(product?.createdBy?.role ?? '')} */}
            </Typography>
          </Box>
          {renderShare()}
        </Stack>

        {/* Organizatör ve değerlendirme bilgileri */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          {entityType === 'aktivite' && renderAuthor()}
          {entityType === 'aktivite' && renderRating()}
        </Stack>

        {/* Açıklama ve detaylar */}
        {renderSubDescription()}
        {renderActivityDetails()}
        {renderReservationButton()}
      </Stack>

      {/* ================================================================ */}
      {/* DIALOG COMPONENTS - Dialog Bileşenleri */}
      {/* ================================================================ */}

      {/* Mesaj oluşturma popup'ı */}
      <CreateMessagePopup
        open={openCreateMessagePopup}
        onClose={() => setOpenCreateMessagePopup(false)}
        activityId={product?._id}
        receiverId={product?.createdBy?._id}
      />

      {/* Ödeme diyaloğu */}
      <Dialog open={!!paymentState.iyzicoUrl} onClose={handleClosePaymentDialog}>
        <DialogTitle>Ödeme</DialogTitle>
        <DialogContent>
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <iframe
              allowFullScreen
              referrerPolicy="origin"
              allow="autoplay *; fullscreen *; encrypted-media *; picture-in-picture *; geolocation *; microphone *; camera *; midi *"
              style={{ width: '600px', height: '800px', border: 'none' }}
              title="iyzico"
              src={`${paymentState.iyzicoUrl}&iframe=true`}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog} color="primary">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Harita diyaloğu */}
      <Dialog fullWidth maxWidth="md" open={openMapDialog} onClose={() => setOpenMapDialog(false)}>
        <DialogTitle>Konum</DialogTitle>
        <DialogContent>
          <ActivitySingleMap activity={product} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMapDialog(false)} color="primary">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Çoklu aktivite dialogları */}
      <ActivityDetailsDialog
        open={openLocationsDialog}
        onClose={() => setOpenLocationsDialog(false)}
        activities={(product as any)?.allActivities || []}
        displayType="locations"
      />
      <ActivityDetailsDialog
        open={openDatesDialog}
        onClose={() => setOpenDatesDialog(false)}
        activities={(product as any)?.allActivities || []}
        displayType="dates"
      />
      <ActivityDetailsDialog
        open={openTimesDialog}
        onClose={() => setOpenTimesDialog(false)}
        activities={(product as any)?.allActivities || []}
        displayType="times"
      />

      {/* Tek aktivite çoklu gün dialogu */}
      <ActivityDetailsDialog
        open={openAllDates}
        onClose={() => setOpenAllDates(false)}
        activities={
          selectedDates && selectedDates.length > 0 && selectedDates[0]?.date
            ? selectedDates
                .filter((date: any) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const dateToCheck = new Date(date.date);
                  dateToCheck.setHours(0, 0, 0, 0);
                  return dateToCheck >= today;
                })
                .map((date: any) => ({
                  ...product,
                  location: product?.location,
                  schedule: {
                    ...product?.schedule,
                    startDate: date.date,
                    days: [date],
                  },
                }))
            : product?.schedule?.days && product.schedule.days.length > 1
              ? (() => {
                  const startDate = new Date(product.schedule.startDate);
                  const activities: any[] = [];

                  product.schedule.days.forEach((day: any, idx: number) => {
                    const currentDate = new Date(startDate);
                    currentDate.setDate(startDate.getDate() + idx);

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (currentDate >= today) {
                      activities.push({
                        ...product,
                        location: product?.location,
                        schedule: {
                          ...product?.schedule,
                          startDate: currentDate.toISOString(),
                          days: [day],
                        },
                      });
                    }
                  });

                  return activities;
                })()
              : product
                ? [product]
                : []
        }
        displayType="dates"
      />
    </Stack>
  );
}
